/**
 * useProcessingGuard.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Optional companion to PersistentRoute.
 *
 * Provides a shared "is something processing right now?" signal that the parent
 * dashboard can read to show a live indicator in the sidebar nav when the user
 * navigates away from an active extraction.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * USAGE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * 1. In VatWorkflow / BankStatementWorkflow:
 *
 *     import { useProcessingGuard } from './useProcessingGuard';
 *     const { setProcessing } = useProcessingGuard('vat');
 *
 *     // Call when extraction starts:
 *     setProcessing(true);
 *
 *     // Call when extraction completes or errors:
 *     setProcessing(false);
 *
 * 2. In ClientDashboard (sidebar nav item):
 *
 *     import { useProcessingStatus } from './useProcessingGuard';
 *     const { isAnyProcessing, processingKeys } = useProcessingStatus();
 *
 *     // Show a pulsing dot on the nav item while running:
 *     <NavItem label="VAT" badge={processingKeys.has('vat') ? '⏳' : null} />
 *
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect, useCallback } from 'react';

// ── Module-level store — survives component unmounts ─────────────────────────
const _listeners = new Set();
let   _state     = {};   // { [key: string]: boolean }

function _notify() {
  _listeners.forEach(fn => fn({ ..._state }));
}

// ── Hook used by workflow components ─────────────────────────────────────────
/**
 * @param {string} key  Unique identifier for this workflow, e.g. 'vat', 'bank'
 */
export function useProcessingGuard(key) {
  const setProcessing = useCallback((active) => {
    if (active) {
      _state = { ..._state, [key]: true };
    } else {
      const next = { ..._state };
      delete next[key];
      _state = next;
    }
    _notify();
  }, [key]);

  // Auto-clear on unmount in case the component is removed while still active.
  useEffect(() => {
    return () => {
      if (_state[key]) {
        const next = { ..._state };
        delete next[key];
        _state = next;
        _notify();
      }
    };
  }, [key]);

  return { setProcessing };
}

// ── Hook used by the nav / dashboard ─────────────────────────────────────────
export function useProcessingStatus() {
  const [snapshot, setSnapshot] = useState({ ..._state });

  useEffect(() => {
    const handler = (s) => setSnapshot(s);
    _listeners.add(handler);
    return () => _listeners.delete(handler);
  }, []);

  const processingKeys = new Set(Object.keys(snapshot));
  return {
    isAnyProcessing: processingKeys.size > 0,
    processingKeys,
  };
}
